/**
 * Voxly — client-side media compressor.
 *
 * Massively speeds up uploads on phone networks:
 *   - 12MP iPhone photo (≈ 8 MB) → ≈ 600 KB at 1920px / quality 0.85
 *   - 4K video → unchanged (hardware-accelerated re-encode is too expensive in JS)
 *
 * The file shape is preserved (still a `File` with name + type + lastModified).
 */

const TARGET_MAX_DIM = 2400; // up from 1920 — sharper photos on 4K phone screens
const TARGET_QUALITY = 0.86;
const SKIP_BELOW_BYTES = 600 * 1024; // don't bother for already-small files
const SKIP_TYPES = new Set(["image/gif", "image/svg+xml"]);

/** Returns true when we have a chance of meaningfully shrinking the file. */
function shouldCompress(file) {
  if (!file || !file.type?.startsWith("image/")) return false;
  if (SKIP_TYPES.has(file.type)) return false;
  if (file.size < SKIP_BELOW_BYTES) return false;
  return true;
}

/** Test once if the browser can encode WebP via canvas.toBlob. */
let _webpSupport = null;
function supportsWebp() {
  if (_webpSupport !== null) return _webpSupport;
  try {
    const c = document.createElement("canvas");
    c.width = 1; c.height = 1;
    _webpSupport = c.toDataURL("image/webp").startsWith("data:image/webp");
  } catch {
    _webpSupport = false;
  }
  return _webpSupport;
}

/** Read a File into an HTMLImageElement via createImageBitmap (fast path) or
 *  Image() fallback. */
async function loadBitmap(file) {
  if (typeof createImageBitmap === "function") {
    try {
      // Cap iOS HEIC weirdness; Safari returns ImageBitmap for JPEG/PNG/WEBP.
      return await createImageBitmap(file);
    } catch {
      /* fall through */
    }
  }
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

/** Compress an image File in-browser. On any failure, returns the original. */
export async function compressImage(file, { maxDim = TARGET_MAX_DIM, quality = TARGET_QUALITY } = {}) {
  if (!shouldCompress(file)) return file;
  try {
    const bitmap = await loadBitmap(file);
    const w = bitmap.width || bitmap.naturalWidth;
    const h = bitmap.height || bitmap.naturalHeight;
    if (!w || !h) return file;

    const scale = Math.min(1, maxDim / Math.max(w, h));
    const targetW = Math.round(w * scale);
    const targetH = Math.round(h * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    if (typeof bitmap.close === "function") bitmap.close();

    // Prefer WebP where supported (≈30% smaller than JPEG at same visual quality).
    // PNG inputs stay PNG to preserve transparency.
    let targetType;
    if (file.type === "image/png") {
      targetType = "image/png";
    } else if (supportsWebp()) {
      targetType = "image/webp";
    } else {
      targetType = "image/jpeg";
    }
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, targetType, targetType === "image/png" ? undefined : quality),
    );
    if (!blob) return file;

    // If the recompressed file is somehow larger (e.g. tiny PNG), keep original.
    if (blob.size >= file.size) return file;

    const ext = targetType === "image/png" ? ".png" : targetType === "image/webp" ? ".webp" : ".jpg";
    const newName = file.name?.replace(/\.[^.]+$/, "") + ext;
    return new File([blob], newName, {
      type: targetType,
      lastModified: file.lastModified ?? Date.now(),
    });
  } catch {
    return file;
  }
}

/** Returns a string like "8.3 MB → 612 KB (-93%)" useful for telemetry/toasts. */
export function describeShrink(originalFile, newFile) {
  if (!originalFile || !newFile) return "";
  const fmt = (n) => (n > 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);
  if (newFile.size === originalFile.size) return fmt(originalFile.size);
  const pct = Math.round((1 - newFile.size / originalFile.size) * 100);
  return `${fmt(originalFile.size)} → ${fmt(newFile.size)} (-${pct}%)`;
}
